/**
 * Responses
 *
 * Displays and manages collected video responses for a campaign. Supports
 * starring, deletion (with confirmation), and read-only permission gating.
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { PageHeader } from '../../../../components/ui/layout/page-header';
import { ArrowLeft, Star, Share2, Shield } from 'lucide-react';
import { SERVER_URL } from '../../../../api/core';
import { auth } from '../../../../firebase/firebase';
import { LoadingSpinner } from '../../../../components/ui/feedback/loading-spinner.jsx';
import BasicLoadingIndicator from '../../../../components/ui/feedback/basic-loading-indicator';
import { ListViewResponse } from '../../../../components/testimonials/responses/ListViewResponse';
import { VideoModal } from '../../../../components/testimonials/responses/VideoModal';
import { ErrorMessage } from '../../../../components/ui/feedback/error-message.jsx';
import { EmptyState } from '../../../../components/ui/feedback/empty-state.jsx';
import { ConfirmationModal } from '../../../../components/ui/modals/confirmation-modal.jsx';
import { useToast } from '../../../../components/ui/notifications/toast-notification.jsx';
import { useNamespace } from '../../../../context/namespace';
import Footer from '../../../../components/shared/Footer';

export default function Responses() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [responses, setResponses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [starredResponses, setStarredResponses] = useState(new Set());
  const [showStarredOnly, setShowStarredOnly] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState(null);
  const { addToast } = useToast();

  // Get user permission from NamespaceContext
  const { userPermission } = useNamespace();
  
  // Check if user has read-only permission
  const hasReadOnlyPermission = userPermission === 'readonly' || userPermission === 'read-only';
  
  // Debug user permission
  console.log('Responses page permission check:', {
    userPermission,
    hasReadOnlyPermission
  });

  // Check for starred filter in URL query params
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const starredParam = searchParams.get('starred');
    if (starredParam === 'true') {
      setShowStarredOnly(true);
    } else if (starredParam === 'false') {
      setShowStarredOnly(false);
    }
    // Only run this effect when the location.search changes
  }, [location.search]);

  useEffect(() => {
    fetchResponses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchResponses = async () => {
    try {
      const idToken = await auth.currentUser.getIdToken();
      const response = await fetch(`${SERVER_URL}/survey/videos/${id}`, {
        headers: { Authorization: `Bearer ${idToken}` }
      });
      if (!response.ok) throw new Error('Failed to fetch responses');
      const data = await response.json();
      const initialStarred = new Set(data.filter(r => r.starred).map(r => r.id));
      setStarredResponses(initialStarred);
      setResponses(data);
    } catch (err) {
      console.error('Error fetching responses:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedResponse) return;
    setIsDeleting(true);
    try {
      const idToken = await auth.currentUser.getIdToken();
      const response = await fetch(`${SERVER_URL}/videoEditor/delete/${selectedResponse.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${idToken}` }
      });
      
      if (!response.ok) throw new Error('Failed to delete video');
      
      // Get the response data
      const responseData = await response.json();
      
      // Show toast notification with the success message
      addToast(responseData.message, "success");
      
      setResponses(prev => prev.filter(r => r.id !== selectedResponse.id));
      setIsDeleteModalOpen(false);
    } catch (err) {
      console.error('Error deleting video:', err);
      addToast(err.message || 'Failed to delete video', "error");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDelete = (response) => {
    setSelectedResponse(response);
    setIsDeleteModalOpen(true);
  };

  const handleStarChange = (responseId, isStarred) => {
    const newStarred = new Set(starredResponses);
    isStarred ? newStarred.add(responseId) : newStarred.delete(responseId);
    setStarredResponses(newStarred);
  };

  // Apply filters based on selected options
  let filteredResponses = responses;
  
  // Apply starred filter if enabled
  if (showStarredOnly) {
    filteredResponses = filteredResponses.filter(response => starredResponses.has(response.id));
  }

  return (
    <div className="p-6 w-full max-w-[1600px] mx-auto">
      <button
        onClick={() => navigate(`/app/campaigns/${id}`)}
        className="mb-6 inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to campaign
      </button>

      <PageHeader
        title="Campaign Responses"
        description="View and manage video testimonials for this campaign."
        className="mb-3"
      >
        <div className="flex gap-2">
          <div
            onClick={() => setShowStarredOnly(!showStarredOnly)}
            className={`inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium transition-colors cursor-pointer ${
              showStarredOnly
                ? 'bg-purple-50 text-purple-600 border-purple-200 dark:bg-purple-900/20 dark:text-purple-400 dark:border-purple-800'
                : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-900 dark:text-gray-300 dark:border-gray-800 dark:hover:bg-gray-800'
            }`}
            title={showStarredOnly ? "Show all responses" : "Show only starred responses"}
          >
            <Star className={`h-4 w-4 ${showStarredOnly ? 'fill-current' : ''}`} />
            Starred
          </div>
        </div>
      </PageHeader>

      {/* Read-only access notification */}
      {hasReadOnlyPermission && (
        <div className="mt-4 mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-gray-700 dark:bg-gray-800/90">
          <div className="flex items-start">
            <div className="mr-3 flex-shrink-0">
              <Shield className="h-5 w-5 text-amber-600 dark:text-amber-500" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-amber-800 dark:text-amber-400">Read-Only Access</h3>
              <div className="mt-1 text-sm text-amber-700 dark:text-gray-300">
                <p>You currently have read-only access to this namespace. Contact an administrator to request edit permissions.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {isLoading ? (
        <BasicLoadingIndicator />
      ) : error ? (
        <ErrorMessage message={error} />
      ) : responses.length === 0 ? (
        <EmptyState
          title="No responses yet"
          description="Share your campaign link to start collecting video responses from your community."
          primaryAction={{
            label: 'Share Campaign',
            onClick: () => navigate(`/app/campaigns/${id}`),
            icon: <Share2 className="h-5 w-5" />
          }}
        />
      ) : filteredResponses.length === 0 && showStarredOnly ? (
        <EmptyState
          title="No starred responses"
          description="Star your favorite responses to find them quickly later."
          icon={Star}
        />
      ) : (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-4">
          {filteredResponses.map((response) => (
            <ListViewResponse
              key={response.id}
              response={response}
              onVideoClick={(response) => setSelectedTestimonial(response)}
              onDelete={handleDelete}
              onStarChange={handleStarChange}
              userPermission={userPermission}
              hasReadOnlyPermission={hasReadOnlyPermission}
            />
          ))}
        </div>
      )}

      {selectedTestimonial && (
        <VideoModal
          testimonial={selectedTestimonial}
          onClose={() => setSelectedTestimonial(null)}
        />
      )}

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Video"
        message="Are you sure you want to delete this video? This action cannot be undone."
        simpleConfirm={true}
        isLoading={isDeleting}
      />

      <Footer />
    </div>
  );
}
